/**
 * <quick-view> — modal that shows the `quick-view` section of `{product.url}?view=quick-view`
 * (falls back to the product page itself when the section cannot be fetched).
 *
 * Contract for sections/quick-view.liquid (built by another agent): render a root element
 * `<div data-quick-view-root>…</div>` with gallery, title, price, a <product-form> and a link to the product page.
 *
 * Triggers: any [data-quick-view="/products/handle"] element, or document event `quickview:open` { url }.
 * Close:    [data-quick-view-close], Esc, backdrop click, or document event `quickview:close`
 *           (product-form dispatches it after a successful add).
 * API:      RM.openQuickView(url, triggerElement)
 */
class QuickView extends HTMLElement {
  #abort = new AbortController();
  #fetchAbort = null;

  connectedCallback() {
    const { signal } = this.#abort;
    this.dialog = this.querySelector('dialog');
    this.content = this.querySelector('[data-quick-view-content]');
    if (!this.dialog || !this.content) return;

    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-quick-view]') : null;
      if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this.open(trigger.getAttribute('data-quick-view'), trigger);
    }, { signal });

    document.addEventListener('quickview:open', (event) => this.open(event.detail && event.detail.url), { signal });
    document.addEventListener('quickview:close', () => this.close(), { signal });

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || (event.target instanceof Element && event.target.closest('[data-quick-view-close]'))) this.close();
    }, { signal });

    this.dialog.addEventListener('close', () => {
      this.#fetchAbort?.abort();
      document.body.classList.remove('has-modal-open');
      this.content.innerHTML = '';
      document.dispatchEvent(new CustomEvent('quickview:closed'));
      if (this.trigger instanceof HTMLElement && document.contains(this.trigger)) this.trigger.focus();
    }, { signal });

    if (window.RM) window.RM.openQuickView = (url, trigger) => this.open(url, trigger);
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  async open(url, trigger) {
    if (!url || !this.dialog) return;
    this.trigger = trigger instanceof HTMLElement ? trigger : document.activeElement;
    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();

    this.content.setAttribute('aria-busy', 'true');
    this.content.innerHTML = '';
    if (!this.dialog.open) {
      this.dialog.showModal();
      document.body.classList.add('has-modal-open');
    }

    try {
      const root = await this.#load(url, this.#fetchAbort.signal);
      if (!root) {
        // Nothing usable came back (template missing, section not rendered): never show a dead modal.
        this.#goToProduct(url);
        return;
      }
      await this.#loadStyles(root);
      if (this.#fetchAbort.signal.aborted) return;
      this.content.innerHTML = '';
      this.content.append(document.importNode(root, true));
      this.content.removeAttribute('aria-busy');
      this.#ensureProductInfo(root.dataset.productInfoSrc);
      this.dialog.querySelector('[data-quick-view-close]')?.focus();
      document.dispatchEvent(new CustomEvent('quickview:opened', { detail: { url } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#goToProduct(url);
    }
  }

  /**
   * Fetch the quick-view section for a product, trying two Section Rendering forms:
   *   1. `{url}?view=quick-view&section_id=quick-view`  (HTML of that single section)
   *   2. `{url}?view=quick-view&sections=quick-view`     (JSON { "quick-view": html })
   * Resolves the [data-quick-view-root] element, or null when neither form yields one.
   */
  async #load(url, signal) {
    const parse = (html) => (html ? new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-view-root]') : null);
    const target = (params) => {
      const next = new URL(url, window.location.origin);
      next.searchParams.set('view', 'quick-view');
      for (const [key, value] of Object.entries(params)) next.searchParams.set(key, value);
      return next.toString();
    };

    try {
      const response = await fetch(target({ section_id: 'quick-view' }), { signal });
      if (response.ok) {
        const root = parse(await response.text());
        if (root) return root;
      }
    } catch (error) {
      if (error.name === 'AbortError') throw error;
    }

    try {
      const response = await fetch(target({ sections: 'quick-view' }), { signal });
      if (response.ok) {
        const data = await response.json();
        const root = parse(data && data['quick-view']);
        if (root) return root;
      }
    } catch (error) {
      if (error.name === 'AbortError') throw error;
    }
    return null;
  }

  /** Make sure the section stylesheet is applied before the markup appears (avoids an unstyled flash). */
  async #loadStyles(root) {
    const links = [...root.querySelectorAll('link[rel="stylesheet"]')];
    await Promise.all(links.map((link) => {
      const href = link.getAttribute('href');
      link.remove();
      if (!href) return null;
      const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find((el) => el.getAttribute('href') === href);
      if (existing) return null;
      return new Promise((resolve) => {
        const el = document.createElement('link');
        el.rel = 'stylesheet';
        el.href = href;
        el.addEventListener('load', resolve, { once: true });
        el.addEventListener('error', resolve, { once: true });
        setTimeout(resolve, 3000);
        document.head.append(el);
      });
    }));
  }

  /** Graceful fallback: close the modal and open the full product page. */
  #goToProduct(url) {
    this.content.removeAttribute('aria-busy');
    this.close();
    window.location.assign(url);
  }

  /** The quick view content uses <product-info>, which is only bundled on product pages: load it on demand. */
  #ensureProductInfo(src) {
    if (!src || customElements.get('product-info') || document.querySelector('script[data-product-info]')) return;
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.setAttribute('data-product-info', '');
    document.head.append(script);
  }

  close() {
    if (this.dialog && this.dialog.open) this.dialog.close();
  }
}

if (!customElements.get('quick-view')) customElements.define('quick-view', QuickView);
